/* eslint-disable camelcase */
import { decamelizeKeys } from '../lib';
import { getBackVersion } from '../config';
import {
    FeatureSet,
    MfaRequired,
    UpdateWorkspaceDataFields,
    WorkspaceSnapshot,
    WorkspaceUserSnapshot,
} from '../models';
import { WorkspaceAclSnapshot } from '../models/Workspace/WorkspaceAcl';
import { WorkspaceLpEventSnapshot } from '../models/Workspace/WorkspaceLpEvent';
import { WorkspaceSurveyControlSnapshot } from '../models/Workspace/WorkspaceSurveyControl';
import {
    WorkspaceUserInviteCreationSnapshot,
    WorkspaceUserInviteSnapshot,
    WorkspaceUserLimit,
} from '../models/Workspace/WorkspaceUserInvite';
import { WorkspaceInviteSnapshot } from '../models/WorkspaceInvite/WorkspaceInvite';
import api from './api';
import { SubscriptionResponse } from './subscription';

type ExtendedWorkspaceSnapshot = WorkspaceSnapshot & {
    subscriptions?: SubscriptionResponse[];
    featureSet: FeatureSet;
};

type WorkspaceResponse = Omit<ExtendedWorkspaceSnapshot, 'users'> & {
    users: WorkspaceUserSnapshot[];
    acl: WorkspaceAclSnapshot;
};

type SimplifiedWorkspace = Omit<WorkspaceSnapshot, 'users'>;

const getWorkspace = (workspaceAlias: number): Promise<WorkspaceResponse> => {
    return api.request({
        method: 'GET',
        url: `${getBackVersion()}/workspace`,
        params: {
            workspace_alias: workspaceAlias,
        },
    });
};

const getAllWorkspaces = (): Promise<SimplifiedWorkspace[]> => {
    return api.request({
        method: 'GET',
        url: `${getBackVersion()}/workspace/list`,
    });
};

const createInvite = (
    invite: WorkspaceUserInviteCreationSnapshot
): Promise<WorkspaceUserSnapshot> => {
    return api.request({
        method: 'POST',
        url: `${getBackVersion()}/workspace/invites`,
        data: decamelizeKeys(invite),
    });
};

const removeUser = (userId: number): Promise<void> => {
    return api.request({
        method: 'DELETE',
        url: `${getBackVersion()}/workspace/users/` + userId,
    });
};

const removeInvite = (inviteId: string): Promise<void> => {
    return api.request({
        method: 'DELETE',
        url: `${getBackVersion()}/workspace/invites/` + inviteId,
    });
};

const updateUserAcl = (
    acl: WorkspaceAclSnapshot,
    userId: number
): Promise<WorkspaceUserSnapshot> => {
    return api.request({
        method: 'PATCH',
        url: `${getBackVersion()}/workspace/users/` + userId,
        data: {
            acl: decamelizeKeys(acl),
        },
    });
};

const updateInviteAcl = (
    acl: WorkspaceAclSnapshot,
    inviteId: string
): Promise<WorkspaceUserInviteSnapshot> => {
    return api.request({
        method: 'PATCH',
        url: `${getBackVersion()}/workspace/invites/` + inviteId,
        data: {
            acl: decamelizeKeys(acl),
        },
    });
};

const getWorkspaceInvite = (
    inviteId: string
): Promise<WorkspaceInviteSnapshot> => {
    return api.request({
        method: 'GET',
        action: 'WORKSPACE_INVITE_GET_REQUEST',
        url: `${getBackVersion()}/workspace/invites/` + inviteId + '/user',
    });
};

const acceptWorkspaceInvite = (inviteId: string): Promise<void> => {
    return api.request({
        method: 'POST',
        url: `${getBackVersion()}/workspace/invites/` + inviteId + '/user',
    });
};

const refuseWorkspaceInvite = (inviteId: string): Promise<void> => {
    return api.request({
        method: 'DELETE',
        action: 'WORKSPACE_INVITE_DELETE_REQUEST',
        url: `${getBackVersion()}/workspace/invites/` + inviteId + '/user',
    });
};

const activateDefaultWorkspace = (): Promise<void> => {
    return api.request({
        method: 'POST',
        url: `${getBackVersion()}/workspace/activate`,
    });
};

const patchWorkspaceInfo = ({
    name,
    email,
    phone,
    doc,
    logoS3Path,
    logoFilename,
    creci,
}: UpdateWorkspaceDataFields): Promise<WorkspaceResponse> =>
    api.request<WorkspaceResponse>({
        method: 'PATCH',
        url: `${getBackVersion()}/workspace`,
        data: {
            name,
            email,
            phone,
            doc,
            creci,
            logo_s3_path: logoS3Path,
            logo_filename: logoFilename,
        },
    });

const mfaRequired = (userId: number): Promise<MfaRequired> => {
    return api.request<MfaRequired>({
        method: 'PATCH',
        url: `${getBackVersion()}/workspace/` + userId,
    });
};

const getWorkspaceSurveyControl = (): Promise<WorkspaceSurveyControlSnapshot> =>
    api.request<WorkspaceSurveyControlSnapshot>({
        method: 'GET',
        url: `${getBackVersion()}/workspace/surveys`,
    });

const setViewedFirstPurchaseSurvey =
    (): Promise<WorkspaceSurveyControlSnapshot> =>
        api.request<WorkspaceSurveyControlSnapshot>({
            method: 'POST',
            url: `${getBackVersion()}/workspace/surveys/first_purchase`,
        });

const getWorkspaceUserLimit = (): Promise<WorkspaceUserLimit> =>
    api.request<WorkspaceUserLimit>({
        method: 'GET',
        url: `${getBackVersion()}/workspace/users/limit`,
    });

const getLastRentalCtaEventDetails = (): Promise<WorkspaceLpEventSnapshot[]> =>
    api.request<WorkspaceLpEventSnapshot[]>({
        method: 'GET',
        url: `${getBackVersion()}/workspace/lp_events`,
    });

const invalidateUserSessions = (userId: number) =>
    api.request<void>({
        method: 'DELETE',
        url: `${getBackVersion()}/workspace/sessions/` + userId,
    });

export default {
    getWorkspace,
    getAllWorkspaces,
    createInvite,
    removeUser,
    removeInvite,
    updateUserAcl,
    updateInviteAcl,
    getWorkspaceInvite,
    acceptWorkspaceInvite,
    refuseWorkspaceInvite,
    activateDefaultWorkspace,
    invalidateUserSessions,
    patchWorkspaceInfo,
    getWorkspaceSurveyControl,
    setViewedFirstPurchaseSurvey,
    getWorkspaceUserLimit,
    getLastRentalCtaEventDetails,
    mfaRequired,
};
export type {
    WorkspaceResponse,
    SimplifiedWorkspace,
    ExtendedWorkspaceSnapshot,
};
